import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, IconButton, Tooltip, Stack, CircularProgress,
  Dialog, DialogContent, Chip, Divider,
} from "@mui/material";
import {
  ChevronLeft, ChevronRight,
  Today as TodayIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  OpenInNew as ViewIcon,
  BookmarkAdd as BookIcon,
  ReportProblem as ConflictIcon,
  NotificationsActive as FollowUpIcon,
  PersonOff as UnassignedIcon,
  CheckCircle as ConfirmedIcon,
  Close as CloseIcon,
  Star as VipIcon,
  CalendarMonth as CalMonthIcon,
  ErrorOutline as WarnIcon,
  Edit as EditIcon,
  TaskAlt as MarkContactedIcon,
  ViewList as ListViewIcon,
  CalendarViewMonth as CalViewIcon,
  AttachMoney as BudgetIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import {
  fetchCalendarEvents, updateLead,
  type CalendarEvent,
} from "./leadApi";
import LeadThreadDrawer from "./LeadThreadDrawer";
import LeadNumberBadge from "./LeadNumberBadge";
import { useLeadBus } from "../../hooks/useLeadBus";

/* ═══════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════════════ */
const NAVY  = "#1E4D8F";
const NAVY2 = "#1a3353";
const GOLD  = "#D4AF37";
const DAYS  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_VISIBLE = 2;

type FilterKey = "followup" | "unassigned" | "confirmed" | "conflict" | "new" | "contacted" | "overdue" | "hot" | "website" | "email" | "phone" | "walk_in" | "referral" | null;
type ViewMode  = "calendar" | "list";
type DateMode  = "inquiry" | "event";

/* ── Safe date formatters (never return "Invalid Date") ─────────────── */
const safeParse = (d: string | null | undefined): Date | null => {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00");
  return isNaN(dt.getTime()) ? null : dt;
};

const fmtDate = (d: string | null | undefined) => {
  const dt = safeParse(d);
  return dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
};

const fmtDateShort = (d: string | null | undefined) => {
  const dt = safeParse(d);
  return dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
};

/* ═══════════════════════════════════════════════════════════════════════
   CARD STYLE MAP
═══════════════════════════════════════════════════════════════════════ */
interface CS {
  main: string; bg: string; border: string;
  badgeBg: string; badgeText: string;
  shadow: string; borderWidth: number;
}

const CARD: Record<string, CS> = {
  confirmed:         { main: "#16a34a", bg: "#f0fdf4", border: "#86efac",  badgeBg: "#16a34a", badgeText: "#fff",    shadow: "0 3px 14px rgba(22,163,74,0.22)",   borderWidth: 3   },
  ready_for_booking: { main: "#0d9488", bg: "#f0fdfa", border: "#5eead4",  badgeBg: "#0d9488", badgeText: "#fff",    shadow: "0 2px 10px rgba(13,148,136,0.18)",  borderWidth: 2.5 },
  negotiation:       { main: "#ea580c", bg: "#fff7ed", border: "#fdba74",  badgeBg: "#ea580c", badgeText: "#fff",    shadow: "0 2px 10px rgba(234,88,12,0.18)",   borderWidth: 2   },
  ready_for_quote:   { main: "#7c3aed", bg: "#f5f3ff", border: "#c4b5fd",  badgeBg: "#7c3aed", badgeText: "#fff",    shadow: "0 2px 8px rgba(124,58,237,0.14)",   borderWidth: 2   },
  contacted:         { main: "#2563eb", bg: "#eff6ff", border: "#93c5fd",  badgeBg: "#2563eb", badgeText: "#fff",    shadow: "0 2px 8px rgba(37,99,235,0.14)",    borderWidth: 2   },
  new:               { main: "#475569", bg: "#f8fafc", border: "#cbd5e1",  badgeBg: "#e2e8f0", badgeText: "#475569", shadow: "0 1px 4px rgba(71,85,105,0.08)",    borderWidth: 1.5 },
  lost:              { main: "#dc2626", bg: "#fef9f9", border: "#fca5a5",  badgeBg: "#fee2e2", badgeText: "#b91c1c", shadow: "0 1px 4px rgba(220,38,38,0.07)",    borderWidth: 1.5 },
};

const INQUIRY_STYLE: CS = {
  main: "#6366f1", bg: "#eef2ff", border: "#c7d2fe",
  badgeBg: "#e0e7ff", badgeText: "#4338ca",
  shadow: "0 1px 5px rgba(99,102,241,0.1)", borderWidth: 1.5,
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmed", ready_for_booking: "Booking Ready",
  negotiation: "Negotiation", ready_for_quote: "Ready for Quote",
  contacted: "Contacted", new: "New", lost: "Lost",
};

const STATUS_SHORT: Record<string, string> = {
  confirmed: "CONFIRMED", ready_for_booking: "BOOKING", negotiation: "NEGOTIATING",
  ready_for_quote: "QUOTING", contacted: "CONTACTED", new: "NEW", lost: "LOST",
};

const PRIORITY_ORDER: Record<string, number> = {
  confirmed: 0, ready_for_booking: 1, negotiation: 2,
  ready_for_quote: 3, contacted: 4, new: 5, lost: 6,
};

const toTitle = (s?: string | null) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : null;

const sortEvs = (evs: CalendarEvent[]): CalendarEvent[] =>
  [...evs].sort((a, b) => {
    if (a.type !== b.type) return a.type === "event_date" ? -1 : 1;
    return (PRIORITY_ORDER[a.status] ?? 9) - (PRIORITY_ORDER[b.status] ?? 9);
  });

const fmtMoney = (v: number | null | undefined) => {
  if (!v) return null;
  return "$" + v.toLocaleString("en-CA", { maximumFractionDigits: 0 });
};

/* ═══════════════════════════════════════════════════════════════════════
   URGENCY
═══════════════════════════════════════════════════════════════════════ */
type Urgency = "overdue" | "today" | "confirmed" | null;

const getUrgency = (ev: CalendarEvent, todayStr: string): Urgency => {
  if (ev.status === "confirmed") return "confirmed";
  if (ev.lead.follow_up_required && ev.lead.follow_up_at) {
    if (ev.lead.follow_up_at < todayStr) return "overdue";
    if (ev.lead.follow_up_at.startsWith(todayStr)) return "today";
  }
  return null;
};

const URGENCY_DOT: Record<NonNullable<Urgency>, { color: string; label: string; icon: string }> = {
  overdue:   { color: "#ef4444", label: "Overdue follow-up",   icon: "🔴" },
  today:     { color: "#f59e0b", label: "Follow-up due today",  icon: "🟡" },
  confirmed: { color: "#16a34a", label: "Confirmed booking",    icon: "✅" },
};

/* ═══════════════════════════════════════════════════════════════════════
   SOURCE CONFIG
═══════════════════════════════════════════════════════════════════════ */
interface SourceMeta { icon: string; label: string; color: string; bg: string }

const SOURCE_CONFIG: Record<string, SourceMeta> = {
  website:  { icon: "🌐", label: "Website",  color: "#2563eb", bg: "#dbeafe" },
  email:    { icon: "📧", label: "Email",    color: "#7c3aed", bg: "#ede9fe" },
  phone:    { icon: "📞", label: "Phone",    color: "#059669", bg: "#d1fae5" },
  walk_in:  { icon: "🚶", label: "Walk-in",  color: "#d97706", bg: "#fef3c7" },
  referral: { icon: "🤝", label: "Referral", color: "#db2777", bg: "#fce7f3" },
  manual:   { icon: "✏️", label: "Manual",   color: "#64748b", bg: "#f1f5f9" },
};

const getSource = (src: string | null | undefined): SourceMeta =>
  SOURCE_CONFIG[src ?? ""] ?? { icon: "❓", label: src ?? "Unknown", color: "#64748b", bg: "#f1f5f9" };

/* ═══════════════════════════════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════════════════════════════════ */
function KpiCard({
  icon, label, value, color, bg, border, active, onClick,
}: {
  icon: React.ReactNode; label: string; value: number;
  color: string; bg: string; border: string;
  active?: boolean; onClick?: () => void;
}) {
  return (
    <Tooltip title={onClick ? `Click to filter: ${label}` : ""} placement="bottom">
      <Box
        onClick={onClick}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          bgcolor: active ? `${color}25` : bg,
          border: `${active ? 2 : 1.5}px solid ${active ? color : border}`,
          borderRadius: "14px", px: 2, py: 1.5,
          flex: "1 1 130px",
          cursor: onClick ? "pointer" : "default",
          transition: "all 0.15s",
          "&:hover": onClick ? { bgcolor: `${color}20`, borderColor: color, transform: "translateY(-1px)" } : {},
          position: "relative", overflow: "hidden",
        }}
      >
        {active && (
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, bgcolor: color, borderRadius: "14px 14px 0 0" }} />
        )}
        <Box sx={{
          width: 40, height: 40, borderRadius: "11px",
          bgcolor: `${color}${active ? "30" : "15"}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Box sx={{ color, "& svg": { fontSize: 21 } }}>{icon}</Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{value}</Typography>
          <Typography sx={{ fontSize: 10.5, color: active ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.55)", fontWeight: active ? 700 : 500, mt: 0.2 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   EVENT CARD
═══════════════════════════════════════════════════════════════════════ */
function EventCard({
  ev, onClick, compact = false, todayStr, onMarkContacted,
}: {
  ev: CalendarEvent; onClick: () => void; compact?: boolean;
  todayStr: string; onMarkContacted?: (id: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isInquiry  = ev.type === "inquiry_received";
  const st         = isInquiry ? INQUIRY_STYLE : (CARD[ev.status] ?? CARD.new);
  const isLost     = ev.status === "lost";
  const isHot      = ev.lead.ai_score === "hot";
  const urgency    = getUrgency(ev, todayStr);

  /* Badge: INQUIRY tag or status short */
  const statusBadge = isInquiry ? "INQUIRY" : (STATUS_SHORT[ev.status] ?? ev.status);

  /* Meta row */
  const eventTypePart = toTitle(ev.event_type);
  const guestPart     = ev.guest_count != null ? `${ev.guest_count} guests` : null;
  const metaLine      = [eventTypePart, guestPart].filter(Boolean).join(" · ");

  /* Budget */
  const budget = ev.lead.budget_known ? fmtMoney(ev.lead.budget_value) : null;

  /* Dates — never show invalid */
  const inquiryDateFmt = fmtDateShort(ev.inquiry_date);
  const eventDateFmt   = fmtDateShort(ev.event_date);
  const followUpDate   = fmtDate(ev.lead.follow_up_at);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      sx={{
        borderRadius: "9px",
        overflow: "hidden",
        bgcolor: st.bg,
        borderLeft: `${st.borderWidth}px solid ${st.main}`,
        border: `1px solid ${urgency === "overdue" ? "#fca5a5" : urgency === "today" ? "#fde68a" : st.border}`,
        borderLeftWidth: `${st.borderWidth}px`,
        cursor: "pointer",
        p: compact ? "7px 9px" : "5px 7px",
        transition: "all 0.15s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: hovered ? `0 6px 22px ${st.main}28` : st.shadow,
        transform: hovered ? "translateY(-1px)" : "none",
        opacity: isLost ? 0.7 : 1,
        position: "relative",
        zIndex: hovered ? 5 : 1,
      }}
    >
      {/* Urgency stripe */}
      {urgency && (
        <Box sx={{ position: "absolute", top: 0, left: st.borderWidth, right: 0, height: 2, bgcolor: URGENCY_DOT[urgency].color }} />
      )}

      {/* Row 1: status badge · urgency icon · hot · vip */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.3, mt: urgency ? 0.4 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          {/* Status tag */}
          <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: st.badgeBg, borderRadius: "4px", px: 0.6, py: "1px" }}>
            <Typography sx={{ fontSize: "0.48rem", fontWeight: 800, color: st.badgeText, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {statusBadge}
            </Typography>
          </Box>
          {/* HOT / urgency indicators */}
          {isHot && (
            <Tooltip title="Hot lead" placement="top">
              <Typography sx={{ fontSize: "0.6rem", lineHeight: 1 }}>🔥</Typography>
            </Tooltip>
          )}
          {urgency && urgency !== "confirmed" && (
            <Tooltip title={URGENCY_DOT[urgency].label} placement="top">
              <Typography sx={{ fontSize: "0.6rem", lineHeight: 1 }}>{URGENCY_DOT[urgency].icon}</Typography>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          {/* Source tag */}
          {(() => {
            const src = getSource(ev.lead.source);
            return (
              <Tooltip title={`Source: ${src.label}`} placement="top">
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "2px",
                  bgcolor: src.bg, borderRadius: "4px", px: 0.5, py: "1px",
                  border: `1px solid ${src.color}30`,
                }}>
                  <Typography sx={{ fontSize: "0.52rem", lineHeight: 1 }}>{src.icon}</Typography>
                  <Typography sx={{ fontSize: "0.45rem", fontWeight: 700, color: src.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {src.label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })()}
          {ev.lead.is_vip && <VipIcon sx={{ fontSize: 9, color: "#f59e0b" }} />}
          {budget && (
            <Tooltip title={`Budget: ${budget}`} placement="top">
              <BudgetIcon sx={{ fontSize: 9, color: "#16a34a" }} />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Row 2: client name */}
      <Typography sx={{
        fontSize: "0.74rem", fontWeight: 800, color: NAVY,
        lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        mb: 0.2,
      }}>
        {toTitle(ev.lead.name)}
      </Typography>

      {/* Row 3: event type + guests */}
      {metaLine && (
        <Typography sx={{ fontSize: "0.59rem", color: "#475569", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mb: 0.15 }}>
          {metaLine}
        </Typography>
      )}

      {/* Row 4: inquiry date (primary) + event date (secondary) */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 0.15 }}>
        {inquiryDateFmt && (
          <Tooltip title="Inquiry date" placement="top">
            <Typography sx={{ fontSize: "0.53rem", color: "#6366f1", fontWeight: 700, display: "flex", alignItems: "center", gap: "2px" }}>
              📥 {inquiryDateFmt}
            </Typography>
          </Tooltip>
        )}
        {eventDateFmt && (
          <Tooltip title="Event date" placement="top">
            <Typography sx={{ fontSize: "0.53rem", color: "#0d9488", fontWeight: 600, display: "flex", alignItems: "center", gap: "2px" }}>
              🎉 {eventDateFmt}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Row 5: budget amount + follow-up */}
      {(budget || (followUpDate && urgency)) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.1 }}>
          {budget && (
            <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, color: "#16a34a" }}>{budget}</Typography>
          )}
          {followUpDate && urgency && urgency !== "confirmed" && (
            <Tooltip title={`Follow-up: ${followUpDate}`} placement="top">
              <Typography sx={{
                fontSize: "0.54rem",
                color: urgency === "overdue" ? "#ef4444" : "#f59e0b",
                fontWeight: 700,
                display: "flex", alignItems: "center", gap: "2px",
              }}>
                <TimeIcon sx={{ fontSize: 8 }} />{followUpDate}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Quick actions — slide in on hover */}
      <Stack
        direction="row" spacing={0.4}
        sx={{
          maxHeight: hovered ? 28 : 0,
          opacity: hovered ? 1 : 0,
          overflow: "hidden",
          transition: "all 0.15s cubic-bezier(0.4,0,0.2,1)",
          mt: hovered ? 0.5 : 0,
          pt: hovered ? 0.4 : 0,
          borderTop: hovered ? `1px solid ${st.border}` : "1px solid transparent",
        }}
      >
        <Tooltip title="View / Edit Lead" placement="top">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onClick(); }}
            sx={{ width: 20, height: 20, bgcolor: st.badgeBg, color: st.badgeText, borderRadius: "5px", p: 0.2, "&:hover": { bgcolor: st.main, color: "#fff" } }}>
            <EditIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Tooltip>
        {ev.lead.phone && (
          <Tooltip title={`Call ${ev.lead.phone}`} placement="top">
            <IconButton size="small" component="a" href={`tel:${ev.lead.phone}`} onClick={e => e.stopPropagation()}
              sx={{ width: 20, height: 20, bgcolor: "#e0f2fe", color: "#0284c7", borderRadius: "5px", p: 0.2, "&:hover": { bgcolor: "#0284c7", color: "#fff" } }}>
              <PhoneIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
        )}
        {ev.status !== "contacted" && ev.status !== "confirmed" && ev.status !== "lost" && onMarkContacted && (
          <Tooltip title="Mark as Contacted" placement="top">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onMarkContacted(ev.lead.id); }}
              sx={{ width: 20, height: 20, bgcolor: "#e0f2fe", color: "#2563eb", borderRadius: "5px", p: 0.2, "&:hover": { bgcolor: "#2563eb", color: "#fff" } }}>
              <MarkContactedIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
        )}
        {!ev.lead.booking_id && ev.status !== "lost" && (
          <Tooltip title="Convert to Booking" placement="top">
            <IconButton size="small" onClick={e => e.stopPropagation()}
              sx={{ width: 20, height: 20, bgcolor: "#dcfce7", color: "#16a34a", borderRadius: "5px", p: 0.2, "&:hover": { bgcolor: "#16a34a", color: "#fff" } }}>
              <BookIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   LIST VIEW ROW
═══════════════════════════════════════════════════════════════════════ */
function ListRow({
  ev, onClick, todayStr, onMarkContacted,
}: {
  ev: CalendarEvent; onClick: () => void;
  todayStr: string; onMarkContacted: (id: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isInquiry = ev.type === "inquiry_received";
  const st        = isInquiry ? INQUIRY_STYLE : (CARD[ev.status] ?? CARD.new);
  const urgency   = getUrgency(ev, todayStr);
  const isHot     = ev.lead.ai_score === "hot";
  const budget    = ev.lead.budget_known ? fmtMoney(ev.lead.budget_value) : null;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2, py: 1.2,
        borderLeft: `3px solid ${st.main}`,
        bgcolor: hovered ? st.bg : "#fff",
        borderBottom: "1px solid #f1f5f9",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
    >
      {/* Date chip */}
      <Box sx={{ minWidth: 64, textAlign: "center", flexShrink: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: ev.start === todayStr ? GOLD : "#475569" }}>
          {fmtDate(ev.start) ?? "—"}
        </Typography>
        <Typography sx={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase" }}>
          {isInquiry ? "Inquiry" : "Event"}
        </Typography>
      </Box>

      <Divider orientation="vertical" flexItem />

      {/* Name + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.2 }}>
          {urgency && urgency !== "confirmed" && (
            <Typography sx={{ fontSize: 10, lineHeight: 1, flexShrink: 0 }}>{URGENCY_DOT[urgency].icon}</Typography>
          )}
          {isHot && <Typography sx={{ fontSize: 10, lineHeight: 1, flexShrink: 0 }}>🔥</Typography>}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {toTitle(ev.lead.name)}
          </Typography>
          {ev.lead.is_vip && <VipIcon sx={{ fontSize: 11, color: "#f59e0b", flexShrink: 0 }} />}
          {/* Public lead number — keeps the calendar list searchable + cite-able. */}
          {ev.lead.lead_number && <LeadNumberBadge value={ev.lead.lead_number} variant="muted" />}
        </Box>
        <Typography sx={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {[toTitle(ev.event_type), ev.guest_count ? `${ev.guest_count} guests` : null, ev.lead.preferred_hall].filter(Boolean).join(" · ")}
        </Typography>
        {/* Inquiry date + event date */}
        <Box sx={{ display: "flex", gap: 1, mt: 0.3 }}>
          {ev.inquiry_date && fmtDateShort(ev.inquiry_date) && (
            <Typography sx={{ fontSize: 10, color: "#6366f1", fontWeight: 600 }}>
              📥 {fmtDateShort(ev.inquiry_date)}
            </Typography>
          )}
          {ev.event_date && fmtDateShort(ev.event_date) && (
            <Typography sx={{ fontSize: 10, color: "#0d9488", fontWeight: 600 }}>
              🎉 {fmtDateShort(ev.event_date)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Status chip */}
      <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: st.badgeBg, borderRadius: "5px", px: 0.8, py: 0.3, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: st.badgeText, textTransform: "uppercase" }}>
          {STATUS_LABELS[ev.status] ?? ev.status}
        </Typography>
      </Box>

      {/* Budget */}
      {budget && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a", flexShrink: 0 }}>{budget}</Typography>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, opacity: hovered ? 1 : 0, transition: "opacity 0.12s" }}>
        <Tooltip title="View Lead">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onClick(); }}
            sx={{ width: 24, height: 24, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "6px", "&:hover": { bgcolor: st.main, color: "#fff" } }}>
            <ViewIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        {ev.lead.phone && (
          <Tooltip title={`Call ${ev.lead.phone}`}>
            <IconButton size="small" component="a" href={`tel:${ev.lead.phone}`} onClick={e => e.stopPropagation()}
              sx={{ width: 24, height: 24, bgcolor: "#e0f2fe", color: "#0284c7", borderRadius: "6px", "&:hover": { bgcolor: "#0284c7", color: "#fff" } }}>
              <PhoneIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        {ev.status !== "contacted" && ev.status !== "confirmed" && ev.status !== "lost" && (
          <Tooltip title="Mark Contacted">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onMarkContacted(ev.lead.id); }}
              sx={{ width: 24, height: 24, bgcolor: "#eff6ff", color: "#2563eb", borderRadius: "6px", "&:hover": { bgcolor: "#2563eb", color: "#fff" } }}>
              <MarkContactedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   FILTER CHIP BAR
═══════════════════════════════════════════════════════════════════════ */
const STATUS_CHIPS: { key: FilterKey; label: string; color: string }[] = [
  { key: null,        label: "All",        color: "#475569" },
  { key: "new",       label: "New",        color: "#475569" },
  { key: "contacted", label: "Contacted",  color: "#2563eb" },
  { key: "overdue",   label: "🔴 Overdue", color: "#ef4444" },
  { key: "hot",       label: "🔥 Hot",     color: "#ea580c" },
];

const SOURCE_CHIPS: { key: FilterKey; label: string; color: string }[] = [
  { key: "website",  label: "🌐 Website",  color: "#2563eb" },
  { key: "email",    label: "📧 Email",    color: "#7c3aed" },
  { key: "phone",    label: "📞 Phone",    color: "#059669" },
  { key: "walk_in",  label: "🚶 Walk-in",  color: "#d97706" },
  { key: "referral", label: "🤝 Referral", color: "#db2777" },
];

/* ═══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════ */
export default function LeadCalendar() {
  const [currentDate, setCurrentDate]   = useState(new Date());
  const [events, setEvents]             = useState<CalendarEvent[]>([]);
  const [loading, setLoading]           = useState(true);
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [selectedId, setSelectedId]     = useState<number | null>(null);
  const [moreModal, setMoreModal]       = useState<{ label: string; events: CalendarEvent[] } | null>(null);
  const [viewMode, setViewMode]         = useState<ViewMode>("calendar");
  const [activeFilter, setActiveFilter] = useState<FilterKey>(null);
  const [dateMode, setDateMode]         = useState<DateMode>("inquiry");

  const year     = currentDate.getFullYear();
  const month    = currentDate.getMonth();
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  /* ── Data loading ─────────────────────────────────────────────────── */
  const loadData = useCallback(async () => {
    setLoading(true);
    const from = new Date(year, month, 1).toISOString().split("T")[0];
    const to   = new Date(year, month + 1, 0).toISOString().split("T")[0];
    try { setEvents(await fetchCalendarEvents(from, to, dateMode)); } catch {}
    setLoading(false);
  }, [year, month, dateMode]);

  useEffect(() => { loadData(); }, [loadData]);

  /* Live sync — event date changes / archival / new leads
     anywhere in the app refresh the calendar grid. */
  useLeadBus(() => { loadData(); });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday   = () => setCurrentDate(new Date());

  /* ── Calendar grid cells ──────────────────────────────────────────── */
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = useMemo(() => {
    const c: { day: number | null; dateStr: string }[] = [];
    for (let i = 0; i < firstDay; i++) c.push({ day: null, dateStr: "" });
    for (let d = 1; d <= daysInMonth; d++) {
      c.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    return c;
  }, [year, month, firstDay, daysInMonth]);

  /* ── KPI computations ─────────────────────────────────────────────── */
  const uniqueLeads = useMemo(
    () => Array.from(new Map(events.map(e => [e.lead.id, e.lead])).values()),
    [events],
  );

  const followUpsToday = useMemo(
    () => uniqueLeads.filter(l => l.follow_up_required && l.follow_up_at?.startsWith(todayStr)).length,
    [uniqueLeads, todayStr],
  );

  const unassignedLeads = useMemo(
    () => uniqueLeads.filter(l => !l.assigned_to).length,
    [uniqueLeads],
  );

  const confirmedCount = useMemo(
    () => events.filter(e => e.status === "confirmed").length,
    [events],
  );

  const conflictDays = useMemo(() => {
    const byDay: Record<string, number> = {};
    events.filter(e => e.type === "event_date").forEach(e => {
      byDay[e.start] = (byDay[e.start] || 0) + 1;
    });
    return Object.values(byDay).filter(c => c > 1).length;
  }, [events]);

  const conflictDaySet = useMemo(() => {
    const byDay: Record<string, number> = {};
    events.filter(e => e.type === "event_date").forEach(e => {
      byDay[e.start] = (byDay[e.start] || 0) + 1;
    });
    return new Set(Object.entries(byDay).filter(([, c]) => c > 1).map(([d]) => d));
  }, [events]);

  /* ── Filter counts (from all leads in month, unaffected by active filter) */
  const filterCounts = useMemo(() => {
    const leads = uniqueLeads;
    return {
      all:       leads.length,
      new:       leads.filter(l => l.status === "new").length,
      contacted: leads.filter(l => l.status === "contacted").length,
      overdue:   leads.filter(l => l.follow_up_required && !!l.follow_up_at && l.follow_up_at < todayStr).length,
      hot:       leads.filter(l => l.ai_score === "hot").length,
      website:   leads.filter(l => l.source === "website").length,
      email:     leads.filter(l => l.source === "email").length,
      phone:     leads.filter(l => l.source === "phone").length,
      walk_in:   leads.filter(l => l.source === "walk_in").length,
      referral:  leads.filter(l => l.source === "referral").length,
    };
  }, [uniqueLeads, todayStr]);

  /* ── Filter logic ─────────────────────────────────────────────────── */
  const filterEvent = useCallback((ev: CalendarEvent): boolean => {
    if (!activeFilter) return true;
    if (activeFilter === "followup")   return ev.lead.follow_up_required && ev.lead.follow_up_at?.startsWith(todayStr) === true;
    if (activeFilter === "unassigned") return !ev.lead.assigned_to;
    if (activeFilter === "confirmed")  return ev.status === "confirmed";
    if (activeFilter === "conflict")   return conflictDaySet.has(ev.start);
    if (activeFilter === "new")        return ev.lead.status === "new";
    if (activeFilter === "contacted")  return ev.lead.status === "contacted";
    if (activeFilter === "overdue")    return ev.lead.follow_up_required && !!ev.lead.follow_up_at && ev.lead.follow_up_at < todayStr;
    if (activeFilter === "hot")        return ev.lead.ai_score === "hot";
    if (activeFilter === "website")    return ev.lead.source === "website";
    if (activeFilter === "email")      return ev.lead.source === "email";
    if (activeFilter === "phone")      return ev.lead.source === "phone";
    if (activeFilter === "walk_in")    return ev.lead.source === "walk_in";
    if (activeFilter === "referral")   return ev.lead.source === "referral";
    return true;
  }, [activeFilter, todayStr, conflictDaySet]);

  const getEventsForDate = useCallback(
    (dateStr: string) => sortEvs(events.filter(e => e.start === dateStr && filterEvent(e))),
    [events, filterEvent],
  );

  /* ── Actions ──────────────────────────────────────────────────────── */
  const openLead = (id: number) => {
    // Calendar rows can be inquiry-/event-/task-/follow_up_task-typed, and
    // the prefixed `ev.id` (e.g. "task-12") is NEVER a valid lead id.
    // We always pass `ev.lead.id` here — this guard catches the case where
    // a future event shape omits the nested lead object.
    if (!Number.isInteger(id) || id <= 0) {
      // eslint-disable-next-line no-console
      console.error("Invalid lead ID passed to drawer (calendar)", id);
      return;
    }
    // eslint-disable-next-line no-console
    console.log("Opening lead drawer (calendar)", id);
    setSelectedId(id);
    setDrawerOpen(true);
  };
  const monthLabel  = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const handleMarkContacted = async (leadId: number) => {
    try { await updateLead(leadId, { status: "contacted" }); loadData(); } catch {}
  };

  const toggleFilter = (key: FilterKey) =>
    setActiveFilter(prev => prev === key ? null : key);

  /* ── Sorted list view events ──────────────────────────────────────── */
  const listEvents = useMemo(() => {
    const filtered = events.filter(filterEvent);
    return [...filtered].sort((a, b) => a.start.localeCompare(b.start));
  }, [events, filterEvent]);

  /* ═══════════════════════════════ RENDER ════════════════════════════ */
  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>

      {/* ══════════════════════ GRADIENT HEADER ════════════════════════ */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 55%, #1e4d7a 100%)`,
        px: { xs: 2, md: 4 }, pt: 3, pb: 3.5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -50, right: -50, width: 220, height: 220, borderRadius: "50%", bgcolor: `${GOLD}08`, pointerEvents: "none" }} />

        {/* Title + navigator row */}
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" mb={2.5} gap={2}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: "13px", bgcolor: `${GOLD}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CalMonthIcon sx={{ fontSize: 22, color: GOLD }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px", lineHeight: 1 }}>
                Lead Calendar
                <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: `${GOLD}cc`, ml: 1.2, verticalAlign: "middle" }}>
                  By {dateMode === "inquiry" ? "Inquiry" : "Event"} Date
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mt: 0.25 }}>
                {events.length} lead{events.length !== 1 ? "s" : ""} · {monthLabel}
                {activeFilter && <Box component="span" sx={{ color: GOLD, fontWeight: 600, ml: 1 }}>· filtered</Box>}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            {/* Date mode toggle */}
            <Box sx={{ display: "flex", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.12)", p: 0.3, mr: 1 }}>
              {(["inquiry", "event"] as DateMode[]).map(m => (
                <Tooltip key={m} title={m === "inquiry" ? "Show by Inquiry Date" : "Show by Event Date"}>
                  <Box
                    onClick={() => setDateMode(m)}
                    sx={{
                      borderRadius: "7px", px: 1.2, py: 0.5, cursor: "pointer",
                      bgcolor: dateMode === m ? GOLD : "transparent",
                      color: dateMode === m ? NAVY : "rgba(255,255,255,0.6)",
                      fontSize: 10.5, fontWeight: 700, lineHeight: 1.4,
                      transition: "all 0.15s",
                      "&:hover": { bgcolor: dateMode === m ? GOLD : "rgba(255,255,255,0.1)" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m === "inquiry" ? "Inquiry Date" : "Event Date"}
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* View toggle */}
            <Box sx={{ display: "flex", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.12)", p: 0.3, mr: 1 }}>
              <Tooltip title="Calendar view">
                <IconButton size="small" onClick={() => setViewMode("calendar")}
                  sx={{ borderRadius: "7px", px: 1, color: viewMode === "calendar" ? NAVY : "rgba(255,255,255,0.6)", bgcolor: viewMode === "calendar" ? GOLD : "transparent", "&:hover": { bgcolor: viewMode === "calendar" ? GOLD : "rgba(255,255,255,0.1)" } }}>
                  <CalViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton size="small" onClick={() => setViewMode("list")}
                  sx={{ borderRadius: "7px", px: 1, color: viewMode === "list" ? NAVY : "rgba(255,255,255,0.6)", bgcolor: viewMode === "list" ? GOLD : "transparent", "&:hover": { bgcolor: viewMode === "list" ? GOLD : "rgba(255,255,255,0.1)" } }}>
                  <ListViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Tooltip title="Previous month">
              <IconButton onClick={prevMonth} size="small" sx={{ color: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.16)", color: "#fff" } }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ px: 2.5, py: 0.8, minWidth: 178, textAlign: "center", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.12)" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff", letterSpacing: "0.2px" }}>{monthLabel}</Typography>
            </Box>

            <Tooltip title="Next month">
              <IconButton onClick={nextMonth} size="small" sx={{ color: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.16)", color: "#fff" } }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Go to today">
              <IconButton onClick={goToday} size="small" sx={{ color: GOLD, bgcolor: `${GOLD}18`, border: `1px solid ${GOLD}35`, borderRadius: "10px", ml: 0.5, "&:hover": { bgcolor: `${GOLD}2c` } }}>
                <TodayIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh">
              <IconButton onClick={loadData} size="small" sx={{ color: "rgba(255,255,255,0.6)", bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.16)" } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* KPI strip */}
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.5}>
          <KpiCard icon={<FollowUpIcon />}   label="Follow-ups Today"   value={followUpsToday}  color="#ef4444" bg="rgba(239,68,68,0.12)"  border="rgba(239,68,68,0.22)"  active={activeFilter === "followup"}   onClick={() => toggleFilter("followup")}   />
          <KpiCard icon={<UnassignedIcon />} label="Unassigned Leads"   value={unassignedLeads} color="#f97316" bg="rgba(249,115,22,0.12)" border="rgba(249,115,22,0.22)" active={activeFilter === "unassigned"} onClick={() => toggleFilter("unassigned")} />
          <KpiCard icon={<ConfirmedIcon />}  label="Confirmed Bookings" value={confirmedCount}  color="#16a34a" bg="rgba(22,163,74,0.12)"  border="rgba(22,163,74,0.22)"  active={activeFilter === "confirmed"}  onClick={() => toggleFilter("confirmed")}  />
          <KpiCard icon={<WarnIcon />}       label="Conflict Days"      value={conflictDays}    color="#eab308" bg="rgba(234,179,8,0.12)"  border="rgba(234,179,8,0.22)"  active={activeFilter === "conflict"}   onClick={() => toggleFilter("conflict")}   />
        </Stack>
      </Box>

      {/* ══════════════════════ CALENDAR BODY ══════════════════════════ */}
      <Box sx={{ px: { xs: 1, md: 3 }, pt: 2, pb: 5 }}>

        {/* Filter chip bar */}
        <Stack direction="column" gap={0.8} mb={1.5}>
          {/* Status filters */}
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.8} alignItems="center">
            {STATUS_CHIPS.map(({ key, label, color }) => {
              const isActive = activeFilter === key;
              const count = (filterCounts as Record<string, number>)[key ?? "all"] ?? filterCounts.all;
              return (
                <Chip
                  key={String(key)}
                  size="small"
                  onClick={() => setActiveFilter(key)}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <span>{label}</span>
                      <Box component="span" sx={{
                        bgcolor: isActive ? "rgba(255,255,255,0.25)" : `${color}18`,
                        color: isActive ? "#fff" : color,
                        borderRadius: "4px", px: "4px", py: "0px",
                        fontSize: "0.65rem", fontWeight: 800, lineHeight: "16px",
                        minWidth: 16, textAlign: "center",
                      }}>
                        {count}
                      </Box>
                    </Box>
                  }
                  sx={{
                    fontSize: 11.5, fontWeight: isActive ? 700 : 500, height: 26,
                    bgcolor: isActive ? color : "#fff", color: isActive ? "#fff" : "#475569",
                    border: `1.5px solid ${isActive ? color : "#e2e8f0"}`, cursor: "pointer",
                    transition: "all 0.13s",
                    "& .MuiChip-label": { px: 1, display: "flex", alignItems: "center" },
                    "&:hover": { bgcolor: isActive ? color : `${color}14`, borderColor: color },
                  }}
                />
              );
            })}
          </Stack>
          {/* Source filters */}
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.8} alignItems="center">
            {SOURCE_CHIPS.map(({ key, label, color }) => {
              const isActive = activeFilter === key;
              const count = key ? ((filterCounts as Record<string, number>)[key] ?? 0) : filterCounts.all;
              return (
                <Chip
                  key={String(key)}
                  size="small"
                  onClick={() => toggleFilter(key)}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <span>{label}</span>
                      <Box component="span" sx={{
                        bgcolor: isActive ? "rgba(255,255,255,0.25)" : `${color}18`,
                        color: isActive ? "#fff" : color,
                        borderRadius: "4px", px: "4px", py: "0px",
                        fontSize: "0.65rem", fontWeight: 800, lineHeight: "16px",
                        minWidth: 16, textAlign: "center",
                      }}>
                        {count}
                      </Box>
                    </Box>
                  }
                  sx={{
                    fontSize: 11.5, fontWeight: isActive ? 700 : 500, height: 26,
                    bgcolor: isActive ? color : "#fff", color: isActive ? "#fff" : "#64748b",
                    border: `1.5px solid ${isActive ? color : "#e2e8f0"}`, cursor: "pointer",
                    transition: "all 0.13s",
                    "& .MuiChip-label": { px: 1, display: "flex", alignItems: "center" },
                    "&:hover": { bgcolor: isActive ? color : `${color}14`, borderColor: color },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
        {/* Compact legend */}
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.8} mb={1.5} alignItems="center">
          {([
            { color: INQUIRY_STYLE.main,    label: "Inquiry" },
            { color: CARD.confirmed.main,   label: "Confirmed" },
            { color: CARD.negotiation.main, label: "Negotiation" },
            { color: CARD.contacted.main,   label: "Contacted" },
            { color: CARD.new.main,         label: "New" },
          ]).map(({ color, label }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "2px", bgcolor: color }} />
              <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>{label}</Typography>
            </Box>
          ))}
        </Stack>

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 16, gap: 2 }}>
            <CircularProgress sx={{ color: GOLD }} size={44} thickness={3} />
            <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>Loading calendar…</Typography>
          </Box>
        ) : viewMode === "list" ? (

          /* ══════════════════ LIST VIEW ══════════════════════════════ */
          <Box sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(13,27,42,0.07)", border: "1px solid #e2e8f0" }}>
            {listEvents.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>No leads match the current filter.</Typography>
              </Box>
            ) : listEvents.map(ev => (
              <ListRow key={ev.id} ev={ev} onClick={() => openLead(ev.lead.id)} todayStr={todayStr} onMarkContacted={handleMarkContacted} />
            ))}
          </Box>

        ) : (

          /* ══════════════════ CALENDAR VIEW ══════════════════════════ */
          <Box sx={{ borderRadius: "18px", overflow: "hidden", boxShadow: "0 4px 32px rgba(13,27,42,0.09), 0 1px 4px rgba(0,0,0,0.04)", border: "1px solid #e2e8f0" }}>

            {/* Day header row */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {DAYS.map((d, i) => (
                <Box key={d} sx={{
                  py: 1.4, textAlign: "center",
                  background: `linear-gradient(180deg, ${NAVY} 0%, ${NAVY2} 100%)`,
                  color: i === 0 || i === 6 ? GOLD : "rgba(255,255,255,0.82)",
                  fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.09em",
                  borderRight: i < 6 ? "1px solid rgba(255,255,255,0.06)" : "none",
                }}>
                  {d}
                </Box>
              ))}
            </Box>

            {/* Day cells */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", bgcolor: "#fff" }}>
              {cells.map((cell, i) => {
                const allEvs     = cell.dateStr ? getEventsForDate(cell.dateStr) : [];
                const isToday    = cell.dateStr === todayStr;
                const isWeekend  = i % 7 === 0 || i % 7 === 6;
                const col        = i % 7;
                const isConflict = cell.dateStr ? conflictDaySet.has(cell.dateStr) : false;
                const visible    = allEvs.slice(0, MAX_VISIBLE);
                const overflow   = allEvs.slice(MAX_VISIBLE);

                const dayLabel = cell.dateStr
                  ? new Date(cell.dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
                  : "";

                return (
                  <Box
                    key={i}
                    sx={{
                      minHeight: { xs: 90, md: 140 },
                      p: "6px 5px 5px",
                      borderRight: col < 6 ? "1px solid #f1f5f9" : "none",
                      borderBottom: "1px solid #f1f5f9",
                      /* Today: gold inset border; conflict: red left border */
                      borderLeft: isConflict ? "3px solid #ef4444" : "none",
                      boxShadow: isToday ? `inset 0 0 0 2px ${GOLD}` : "none",
                      bgcolor: !cell.day
                        ? "#f8fafc"
                        : isToday    ? "#fffcf0"
                        : isConflict ? "rgba(239,68,68,0.04)"
                        : isWeekend  ? "#fafafa"
                        : "#fff",
                      transition: "background 0.12s",
                      "&:hover": cell.day ? { bgcolor: isToday ? "#fff8e6" : isConflict ? "rgba(239,68,68,0.07)" : "#f8f9ff" } : {},
                      position: "relative",
                    }}
                  >
                    {cell.day && (
                      <>
                        {/* Day number + badge row */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.6 }}>
                          <Box sx={{
                            width: 24, height: 24, borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: isToday ? GOLD : "transparent",
                            boxShadow: isToday ? `0 2px 8px ${GOLD}55` : "none",
                          }}>
                            <Typography sx={{
                              fontSize: "0.7rem",
                              fontWeight: isToday ? 800 : isWeekend ? 600 : 500,
                              color: isToday ? "#fff" : isWeekend ? "#94a3b8" : "#475569",
                              lineHeight: 1,
                            }}>
                              {cell.day}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.3} alignItems="center">
                            {isConflict && (
                              <Tooltip title={`${allEvs.length} leads on this day`}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, bgcolor: "#fee2e2", borderRadius: "4px", px: 0.5, py: "1px" }}>
                                  <ConflictIcon sx={{ fontSize: 9, color: "#ef4444" }} />
                                  <Typography sx={{ fontSize: "0.52rem", fontWeight: 800, color: "#ef4444" }}>{allEvs.length}</Typography>
                                </Box>
                              </Tooltip>
                            )}
                            {allEvs.length > 0 && (
                              <Box sx={{
                                bgcolor: allEvs.every(e => e.type === "inquiry_received") ? "#6366f1" : NAVY,
                                color: "#fff", borderRadius: "5px",
                                px: 0.6, minWidth: 16, height: 16,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.52rem", fontWeight: 700,
                              }}>
                                {allEvs.length}
                              </Box>
                            )}
                          </Stack>
                        </Box>

                        {/* Visible event cards (max 2) */}
                        <Stack spacing={0.4}>
                          {visible.map(ev => (
                            <EventCard
                              key={ev.id}
                              ev={ev}
                              onClick={() => openLead(ev.lead.id)}
                              todayStr={todayStr}
                              onMarkContacted={handleMarkContacted}
                            />
                          ))}
                        </Stack>

                        {/* +X more → opens side panel */}
                        {overflow.length > 0 && (
                          <Box
                            onClick={() => setMoreModal({ label: dayLabel, events: allEvs })}
                            sx={{
                              mt: 0.5, py: 0.4, textAlign: "center",
                              borderRadius: "6px", cursor: "pointer",
                              bgcolor: "#f1f5f9", border: "1px dashed #cbd5e1",
                              "&:hover": { bgcolor: "#eef2ff", borderColor: "#6366f1", "& span": { color: "#6366f1" } },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#64748b" }}>
                              +{overflow.length} more
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* ══════════════════════ "+X MORE" SIDE PANEL ══════════════════ */}
      <Dialog
        open={!!moreModal}
        onClose={() => setMoreModal(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px", overflow: "hidden",
            position: "fixed", right: 16, top: 80, bottom: 16, m: 0,
            maxHeight: "calc(100vh - 96px)",
          },
        }}
      >
        <Box sx={{ px: 3, py: 2.5, background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)`, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>{moreModal?.label}</Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{moreModal?.events.length} leads on this day</Typography>
          </Box>
          <IconButton onClick={() => setMoreModal(null)} size="small" sx={{ color: "rgba(255,255,255,0.55)", mt: -0.3 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2, bgcolor: "#f8fafc" }}>
          <Stack spacing={0.7}>
            {moreModal?.events.map(ev => (
              <EventCard key={ev.id} ev={ev} compact todayStr={todayStr} onMarkContacted={handleMarkContacted}
                onClick={() => { setMoreModal(null); openLead(ev.lead.id); }} />
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════ LEAD DRAWER ════════════════════════════ */}
      <LeadThreadDrawer open={drawerOpen} threadId={selectedId} onClose={() => setDrawerOpen(false)} onUpdated={loadData} />
    </Box>
  );
}
